import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserCheck, Search, Check, Download } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

const fold = (s) => (s || "").toLocaleLowerCase("tr");

export default function OfficerAssign() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null); // {committee, ids}
  const [uq, setUq] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/election-day/officers").then(({ data }) => setD(data))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail, e)));

  useEffect(() => { load(); }, []);

  const list = useMemo(() => {
    if (!d) return [];
    if (!q.trim()) return d.committees;
    return d.committees.filter((c) => fold(c.name).includes(fold(q)) || String(c.committee_no) === q.trim());
  }, [d, q]);

  const users = useMemo(() => {
    if (!d) return [];
    if (!uq.trim()) return d.users;
    return d.users.filter((u) => fold(u.name).includes(fold(uq)));
  }, [d, uq]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/election-day/officers/${edit.committee.committee_no}`, { user_ids: edit.ids });
      toast.success("Sandık görevlileri kaydedildi");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  if (!d) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...</div>;

  const assigned = d.committees.filter((c) => c.officers.length).length;

  return (
    <div className="space-y-4" data-testid="officer-assign">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">
          Her komiteye sandık görevlisi atayın. Atanan kişi, komite yetkisi olmasa bile o komitenin
          sandık ekranına girip oy işaretleyebilir. <b className="text-foreground">{assigned}/{d.committees.length}</b> komite atanmış.
        </p>
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Komite ara"
            className="pl-8" data-testid="officer-search" />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => (
          <Card key={c.committee_no} className="p-3.5 border border-border" data-testid={`officer-card-${c.committee_no}`}>
            <p className="font-display font-bold text-sm leading-tight">{c.committee_no}. {c.name}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{c.seats} meclis üyeliği</p>
            <div className="mt-2 flex flex-wrap gap-1.5 min-h-[26px]">
              {c.officers.length === 0
                ? <span className="text-xs text-muted-foreground">Görevli atanmadı</span>
                : c.officers.map((o) => (
                  <Badge key={o.id} variant="secondary" className="text-[10px]">
                    <UserCheck className="h-3 w-3 mr-1" />{o.name}
                  </Badge>
                ))}
            </div>
            <Button size="sm" variant="outline" className="mt-2.5 h-8 text-xs flex-1"              data-testid={`officer-edit-${c.committee_no}`}
              onClick={() => { setEdit({ committee: c, ids: [...c.officer_ids] }); setUq(""); }}>
              Görevli ata
            </Button>
            {c.officers.length > 0 && (
              <Button size="sm" variant="ghost" className="mt-2.5 h-8 px-2"
                data-testid={`officer-card-pdf-${c.committee_no}`}
                title="Sandık görevlisi kartı (PDF)"
                onClick={() => {
                  const token = localStorage.getItem("imeak_token");
                  fetch(`${API}/election-day/officer-cards?committee_no=${c.committee_no}`,
                    { headers: { Authorization: `Bearer ${token}` } })
                    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
                    .then((b) => {
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(b);
                      a.download = `GOREVLI_KARTI_K${c.committee_no}.pdf`;
                      a.click();
                    })
                    .catch(() => toast.error("Kart indirilemedi"));
                }}>
                <Download className="h-3.5 w-3.5" />
              </Button>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent data-testid="officer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {edit?.committee.committee_no}. {edit?.committee.name} — sandık görevlileri
            </DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={uq} onChange={(e) => setUq(e.target.value)} placeholder="Kullanıcı ara"
              className="pl-8" data-testid="officer-user-search" />
          </div>
          <ScrollArea className="h-[320px] -mx-1 px-1">
            {users.map((u) => {
              const on = edit?.ids.includes(u.id);
              return (
                <button key={u.id} type="button" data-testid={`officer-pick-${u.id}`}
                  onClick={() => setEdit((s) => ({
                    ...s, ids: on ? s.ids.filter((i) => i !== u.id) : [...s.ids, u.id],
                  }))}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 mb-1.5 text-left text-sm ${
                    on ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-border hover:bg-muted/50"}`}>
                  <span>
                    <span className="font-medium">{u.name}</span>
                    <span className="block text-[11px] text-muted-foreground">{u.role_label}</span>
                  </span>
                  {on && <Check className="h-4 w-4 text-emerald-600" />}
                </button>
              );
            })}
          </ScrollArea>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Vazgeç</Button>
            <Button onClick={save} disabled={saving} data-testid="officer-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Kaydet ({edit?.ids.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
